import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, X, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DEFAULT_CONTENT, SECTION_SCHEMA } from "@/constants/content";

export default function ContentManager() {
  const queryClient = useQueryClient();
  const { data: content = {} } = useQuery({
    queryKey: ["content"],
    queryFn: async () => (await api.get("/content")).data,
  });

  const [section, setSection] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const open = (key) => {
    const schema = SECTION_SCHEMA.find((s) => s.key === key);
    setSection(schema);
    setForm({ ...(DEFAULT_CONTENT[key] || {}), ...(content[key] || {}) });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/content/${section.key}`, { value: form });
      await queryClient.invalidateQueries({ queryKey: ["content"] });
      toast.success(`Sezione "${section.label}" aggiornata`);
      setSection(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [fieldName]: data.image_url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mt-14" data-testid="content-manager">
      <h2 className="font-display text-2xl font-bold text-[#F4EBE1]">Testi e Immagini del Sito</h2>
      <p className="text-xs text-[#78746D] mt-1">
        Modifica i contenuti di ogni sezione della home. Le modifiche sono subito visibili sul sito.
      </p>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="content-sections-grid">
        {SECTION_SCHEMA.map((s) => (
          <button
            key={s.key}
            data-testid={`edit-section-${s.key}`}
            onClick={() => open(s.key)}
            className="leather-card p-4 text-left group"
          >
            <p className="text-sm font-semibold text-[#F4EBE1] group-hover:text-[#C5A059] transition-colors">
              {s.label}
            </p>
            <p className="mt-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#78746D]">
              <Pencil className="w-3 h-3" /> Modifica
            </p>
          </button>
        ))}
      </div>

      {section && form && (
        <div className="fixed inset-0 z-[85] bg-[#0A0A0B]/90 backdrop-blur-md flex items-center justify-center p-4">
          <form
            onSubmit={save}
            data-testid="content-form-modal"
            className="w-full max-w-lg bg-[#121215] border border-[#C5A059]/30 p-6 space-y-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#F4EBE1]">{section.label}</h3>
              <button
                type="button"
                onClick={() => setSection(null)}
                className="p-2 text-[#C0B9B0] hover:text-[#C5A059]"
                aria-label="Chiudi"
                data-testid="content-form-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {section.fields.map((f) => (
              <div key={f.name}>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#78746D] mb-1.5">
                  {f.label}
                </label>
                {f.type === "textarea" && (
                  <textarea
                    data-testid={`content-field-${f.name}`}
                    rows={4}
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1] resize-none"
                  />
                )}
                {f.type === "input" && (
                  <input
                    data-testid={`content-field-${f.name}`}
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]"
                  />
                )}
                {f.type === "image" && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        data-testid={`content-field-${f.name}`}
                        value={form[f.name] || ""}
                        onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                        placeholder="URL immagine o carica un file"
                        className="flex-1 bg-[#0A0A0B] border border-[#2A2A30] focus:border-[#C5A059] outline-none px-4 py-3 text-sm text-[#F4EBE1]"
                      />
                      <label
                        data-testid={`content-upload-${f.name}`}
                        className={`shrink-0 flex items-center gap-2 px-4 py-3 border border-[#C5A059]/40 text-[#C5A059] font-mono text-xs uppercase tracking-[0.15em] cursor-pointer hover:border-[#C5A059] transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                      >
                        <Upload className="w-4 h-4" />
                        {uploading ? "..." : "Carica"}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={(e) => uploadImage(e, f.name)}
                        />
                      </label>
                    </div>
                    {form[f.name] && (
                      <img
                        src={form[f.name]}
                        alt="Anteprima"
                        className="w-full h-40 object-cover border border-[#2A2A30]"
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button
                data-testid="save-content-button"
                type="submit"
                disabled={saving}
                className="flex-1 py-4 bg-[#C5A059] hover:bg-[#E2C787] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] font-bold transition-colors disabled:opacity-50"
              >
                {saving ? "Salvataggio..." : "Salva Modifiche"}
              </button>
              <button
                type="button"
                data-testid="cancel-content-button"
                onClick={() => setSection(null)}
                className="px-6 py-4 border border-[#2A2A30] text-[#C0B9B0] font-mono text-xs uppercase tracking-[0.2em] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
              >
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
